import axiosInstance from "../../axios/axiosInstance";

export interface Role {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  permissions: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Permission {
  id: number;
  name: string;
  description: string | null;
  category: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const roleApi = {
  getAllRoles: async (search?: string, excludeSuperAdmin?: boolean): Promise<Role[]> => {
    try {
      const params = new URLSearchParams();
      if (search) {
        params.append("search", search);
      }
      if (excludeSuperAdmin) {
        params.append("excludeSuperAdmin", "true");
      }
      const url = params.toString() ? `/api/role?${params.toString()}` : `/api/role`;
      const resp = await axiosInstance.get<{ status: string; data: Role[] }>(url);
      return resp.data.data || [];
    } catch (error: any) {
      console.error("Error fetching roles:", error);
      throw new Error(error.response?.data?.message || "Failed to fetch roles");
    }
  },

  getRoleById: async (id: number): Promise<Role> => {
    try {
      const resp = await axiosInstance.get<{ status: string; data: Role }>(`/api/role/${id}`);
      return resp.data.data;
    } catch (error: any) {
      console.error("Error fetching role:", error);
      throw new Error(error.response?.data?.message || "Failed to fetch role");
    }
  },

  createRole: async (data: Omit<Role, "id" | "slug" | "created_at" | "updated_at">): Promise<Role> => {
    try {
      const resp = await axiosInstance.post<{ status: string; data: Role }>(`/api/role`, data);
      return resp.data.data;
    } catch (error: any) {
      console.error("Error creating role:", error);
      throw new Error(error.response?.data?.message || "Failed to create role");
    }
  },

  updateRole: async (id: number, data: Partial<Omit<Role, "id" | "slug" | "created_at" | "updated_at">>): Promise<Role> => {
    try {
      const resp = await axiosInstance.put<{ status: string; data: Role }>(`/api/role/${id}`, data);
      return resp.data.data;
    } catch (error: any) {
      console.error("Error updating role:", error);
      throw new Error(error.response?.data?.message || "Failed to update role");
    }
  },

  deleteRole: async (id: number): Promise<void> => {
    try {
      await axiosInstance.delete(`/api/role/${id}`);
    } catch (error: any) {
      console.error("Error deleting role:", error);
      throw new Error(error.response?.data?.message || "Failed to delete role");
    }
  },

  getAllPermissions: async (): Promise<Permission[]> => {
    try {
      const resp = await axiosInstance.get<{ status: string; data: Permission[] }>(`/api/permission`);
      return resp.data.data || [];
    } catch (error: any) {
      console.error("Error fetching permissions:", error);
      throw new Error(error.response?.data?.message || "Failed to fetch permissions");
    }
  },

  seedPermissions: async (): Promise<void> => {
    try {
      await axiosInstance.post(`/api/permission/seed`);
    } catch (error: any) {
      console.error("Error seeding permissions:", error);
      throw new Error(error.response?.data?.message || "Failed to seed permissions");
    }
  },

  seedRole: async (roleData: Omit<Role, "id" | "slug" | "created_at" | "updated_at">): Promise<Role> => {
    try {
      return await roleApi.createRole(roleData);
    } catch (error: any) {
      console.error("Error seeding role:", error);
      throw new Error(error.response?.data?.message || "Failed to seed role");
    }
  },
};
